import React from 'react';
import useDataGrid from '@afosto/utils-react/datagrid/useDataGrid';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useShipmentContext } from '../../../components/ShipmentProvider';
import Avatar from '@material-ui/core/Avatar';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Hidden from '@material-ui/core/Hidden';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import { DataGrid } from '@afosto/app-components-react/DataGrid';
import DataList from '@afosto/app-components-react/DataList';
import SkeletonBody from '@afosto/components-react/SkeletonBody';
import dataSourceRequest from '@afosto/utils-react/dataSource/dataSourceRequest';
import fetchMultipleProducts from '@afosto/utils-react/fetchMultipleProducts';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { search as searchApi } from '@afosto/fulfilment/api/Search';
import { productImageFormatter, productSkuFormatter } from './formatters';

const DataListContainerGrid = props => <Grid container spacing={1} {...props} />;

const ShipmentItems = () => {
  const intl = useIntl();
  const { shipment = {} } = useShipmentContext();

  const fetchItems = async dataSource =>
    dataSourceRequest(
      dataSource,
      async (data, requestOptions) =>
        searchApi.listListItems(
          {
            constraints: [
              {
                key: 'shipment_ids',
                operator: 'in',
                value: [shipment?.id],
              },
            ],
          },
          requestOptions,
        ),
      {
        transformData: async (data, source) => {
          const { pagination = {} } = source ?? {};
          const { itemsPerPage } = pagination;
          const skus = data.map(item => item.sku);
          const products = await fetchMultipleProducts(
            skus,
            {},
            {
              headers: {
                'x-page-size': itemsPerPage,
              },
            },
          );

          return data.reduce((acc, item) => {
            const [id] = item?.ids ?? [];
            const product = findObjectInArray(products, 'sku', item.sku);
            const positions = (item?.positions ?? []).map(position => position.code);

            return [
              ...acc,
              {
                ...item,
                id,
                product,
                positions,
                total_boxed: item?.totals?.boxed,
                total_listed: item?.totals?.listed,
                total_handled: item?.totals?.handled,
                total_missing: item?.totals?.missing,
              },
            ];
          }, []);
        },
      },
    );

  const dataSource = useDataGrid(fetchItems, {
    modules: ['dataProperties', 'pagination'],
    dataProperties: {
      fixedProperties: [
        {
          key: 'image',
          name: '',
          type: 'productImage',
          width: '50px',
        },
        {
          key: 'sku',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingItems.sku',
            defaultMessage: 'SKU',
            description: 'The column name for column sku',
          }),
          type: 'productSku',
        },
        {
          key: 'description',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingItems.description',
            defaultMessage: 'Description',
            description: 'The column name for column description',
          }),
        },
        {
          key: 'quantity',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingItems.quantity',
            defaultMessage: 'Quantity',
            description: 'The column name for column quantity',
          }),
        },
        {
          key: 'positions',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingItems.positions',
            defaultMessage: 'Positions',
            description: 'The column name for column positions',
          }),
        },
        {
          key: 'total_listed',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingItems.totalListed',
            defaultMessage: 'Listed',
            description: 'The column name for column total listed',
          }),
        },
        {
          key: shipment?.is_outbound ? 'total_handled' : 'total_boxed',
          name: intl.formatMessage({
            id: `dataGrid.columns.shippingItems.${
              shipment?.is_outbound ? 'totalPicked' : 'totalCounted'
            }`,
            defaultMessage: shipment?.is_outbound ? 'Picked' : 'Counted',
            description: 'The column name for column total boxed',
          }),
        },
        {
          key: shipment?.is_outbound ? 'total_boxed' : 'total_handled',
          name: intl.formatMessage({
            id: `dataGrid.columns.shippingItems.${
              shipment?.is_outbound ? 'totalBoxed' : 'totalPlaced'
            }`,
            defaultMessage: shipment?.is_outbound ? 'Boxed' : 'Placed',
            description: 'The column name for column total boxed',
          }),
        },
        {
          key: 'total_missing',
          name: intl.formatMessage({
            id: 'dataGrid.columns.shippingItems.totalMissing',
            defaultMessage: 'Missing',
            description: 'The column name for column total missing',
          }),
        },
      ],
    },
  });

  /**
   * Render a shipment item in the data list.
   * @param {object} item
   * @returns {*}
   */
  const renderItem = item => (
    <Grid key={item.id} item xs={12}>
      <Card>
        <CardContent>
          <List disablePadding>
            <ListItem disableGutters>
              <ListItemAvatar>
                <Avatar src={item.product && item.product.image} />
              </ListItemAvatar>
              <ListItemText primary={item.description} secondary={item.sku} />
            </ListItem>
          </List>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <Typography variant="overline" color="textSecondary">
                {intl.formatMessage({
                  id: 'dataGrid.columns.shippingItems.quantity',
                  defaultMessage: 'Quantity',
                  description: 'The column name for column quantity',
                })}
              </Typography>
              <Typography variant="body2" color="textPrimary">
                {item.quantity}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="overline" color="textSecondary">
                {intl.formatMessage({
                  id: 'dataGrid.columns.shippingItems.totalListed',
                  defaultMessage: 'Listed',
                  description: 'The column name for column total listed',
                })}
              </Typography>
              <Typography variant="body2" color="textPrimary">
                {item.total_listed}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <Typography variant="overline" color="textSecondary">
                {intl.formatMessage(
                  shipment?.is_outbound
                    ? {
                        id: 'dataGrid.columns.shippingItems.totalBoxed',
                        defaultMessage: 'Packed',
                        description: 'The column name for column total boxed',
                      }
                    : {
                        id: 'dataGrid.columns.shippingItems.totalPlaced',
                        defaultMessage: 'Placed',
                        description: 'The column name for column total placed',
                      },
                )}
              </Typography>
              <Typography variant="body2" color="textPrimary">
                {shipment?.is_outbound ? item.total_boxed : item.total_handled}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  /**
   * Render the skeleton for the shipment item in the data list.
   * @param {string} key
   * @returns {*}
   */
  const renderItemSkeleton = key => (
    <Grid key={key} item xs={12}>
      <Card>
        <CardContent>
          <List disablePadding>
            <ListItem disableGutters>
              <ListItemText>
                <SkeletonBody lines={2} />
              </ListItemText>
            </ListItem>
          </List>
          <Grid container spacing={1}>
            <Grid item xs={4}>
              <SkeletonBody lines={1} />
            </Grid>
            <Grid item xs={4}>
              <SkeletonBody lines={1} />
            </Grid>
            <Grid item xs={4}>
              <SkeletonBody lines={1} />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <>
      <Hidden implementation="js" mdUp>
        <DataList
          dataSource={dataSource}
          renderItem={renderItem}
          component={DataListContainerGrid}
          renderSkeletonItem={renderItemSkeleton}
        />
      </Hidden>
      <Hidden implementation="js" smDown>
        <DataGrid
          dataSource={dataSource}
          formatters={{ productImage: productImageFormatter, productSku: productSkuFormatter }}
        />
      </Hidden>
    </>
  );
};

export default ShipmentItems;
